import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/home/circleci/test-ci-code/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h1>
    <h2 {...{
      "id": "installation"
    }}>{`Installation`}</h2>
    <p>{`Getting started with `}<a parentName="p" {...{
        "href": "https://github.com/Lemoncode/fonk"
      }}><strong parentName="a">{`Fonk`}</strong></a>{` is something really easy and quick. First of all you will need to install `}<strong parentName="p">{`Fonk`}</strong>{` in your existing project using your favorite package manager (we'll assume npm for this example).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install @lemoncode/fonk --save
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Fonk`}</strong>{` is Typescript friendly, typing definitions are already included in the main package.`}</p>
    </blockquote>
    <h2 {...{
      "id": "using"
    }}>{`Using`}</h2>
    <p>{`Fonk is framework agnostic, it just works out of the box (no dependencies).`}</p>
    <p>{`We are going to start by adding some lines of code to define a login validation record:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const loginRecord = {
  user: '',
  password: '',
};
`}</code></pre>
    <p>{`Now we are going to define the following validation schema for this form:`}</p>
    <p>{`- `}<strong parentName="p">{`User field`}</strong>{` is a required field.`}</p>
    <p>{`- `}<strong parentName="p">{`Password`}</strong>{` field is a required field.`}</p>
    <p>{`Let's get started with Fonk, it is time to create the validation schema:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import { Validators, createFormValidation } from '@lemoncode/fonk';

const validationSchema = {
  field: {
    user: [Validators.required],
    password: [Validators.required],
  },
};

const formValidation = createFormValidation(validationSchema);
`}</code></pre>
    <p>{`And let's fire a field validation (usually we will fire this in the `}<em parentName="p">{`onChange`}</em>{` or `}<em parentName="p">{`onBlur`}</em>{` event of every HTML field):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`formValidation
  .validateField('user', loginRecord.user)
  .then(validationResult => {
    console.log(validationResult);
  });
`}</code></pre>
    <p>{`Since the record field user is empty, we will get the following output from the `}<em parentName="p">{`console.log(validationResult)`}</em>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`{
  succeeded: false,
  message: "Please fill in this mandatory field.",
  type: "REQUIRED"
}
`}</code></pre>
    <p>{`What happens if we feed the login.user field with data?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`loginRecord.user = 'John';
`}</code></pre>
    <p>{`And we fire again the field validation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`formValidation
  .validateField('user', loginRecord.user)
  .then(validationResult => {
    console.log(validationResult);
  });
`}</code></pre>
    <p>{`We get the following result (validation succeeded):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`{
  succeeded: true,
  message: "",
  type: "REQUIRED"
}
`}</code></pre>
    <p>{`If you want to fire all the form validations in one go, you can execute `}<em parentName="p">{`validateForm`}</em></p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`formValidation.validateForm(loginRecord).then(validationResult => {
  console.log(validationResult);
});
`}</code></pre>
    <p>{`And you will get the result of firing all defined validations in the schema (in this case `}<em parentName="p">{`user`}</em>{` has been
already informed and `}<em parentName="p">{`password`}</em>{` field is empty):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`{
  succeeded: false,
  fieldErrors: {
    user: {
      succeeded: true,
      message: "",
      type: "REQUIRED"
    },
    password: {
      succeeded: false,
      message: "Please fill in this mandatory field.",
      type: "REQUIRED"
    },
  },
  recordErrors: {}
}
`}</code></pre>
    <h2 {...{
      "id": "ui-examples"
    }}>{`UI Examples`}</h2>
    <p><img alt="validate-field" src={require("../gifs/validate-field.gif")} /></p>
    <p>{`You can play with this example in the following sandbox `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/vanilla/js/validate-field"
      }}>{`validate-field js`}</a>{` and `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/vanilla/js/validate-form"
      }}>{`validate-form js`}</a>{`.`}</p>
    <p>{`Typescript version: `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/vanilla/ts/validate-field"
      }}>{`validate-field ts`}</a>{` and `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/vanilla/ts/validate-form"
      }}>{`validate-form ts`}</a></p>
    <p>{`If you want to check a full example including user interface interaction, check the following sandboxes:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Plain vanilla js example: `}<a parentName="p" {...{
            "href": "https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/vanilla/js/validate-field"
          }}>{`https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/vanilla/js/validate-field`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`React + React Final Form: `}<a parentName="p" {...{
            "href": "https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/react-final-form/js/validate-field"
          }}>{`https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/react-final-form/js/validate-field`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`React + Formik: `}<a parentName="p" {...{
            "href": "https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/formik/js/basic"
          }}>{`https://codesandbox.io/s/github/lemoncode/fonk/tree/master/examples/formik/js/basic`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Vuejs: `}<a parentName="p" {...{
            "href": "https://codesandbox.io/s/github/Lemoncode/fonk/tree/master/examples/vue/js/validate-field"
          }}>{`https://codesandbox.io/s/github/Lemoncode/fonk/tree/master/examples/vue/js/validate-field`}</a></p>
      </li>
    </ul>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h2>
    <p>{`Now many questions will be arising in your head:`}</p>
    <ul>
      <li parentName="ul">{`Can I define more than one field validation per field?`}</li>
      <li parentName="ul">{`Where can I find more validations already implemented? (we call this `}<em parentName="li">{`validators`}</em>{`).`}</li>
      <li parentName="ul">{`How can I create my custom `}<em parentName="li">{`validator`}</em>{`?`}</li>
      <li parentName="ul">{`What about asynchronous validations?`}</li>
      <li parentName="ul">{`And global form validations?(we call them record level validations).`}</li>
      <li parentName="ul">{`Does it integrate well with libraries like React Final Form?`}</li>
      <li parentName="ul">{`What about multilanguage support?`}</li>
    </ul>
    <p>{`All this cases are covered by the library, let's jump into the next topic
`}<a parentName="p" {...{
        "href": "/fonk-doc/validation-schema"
      }}>{`Validation Schema`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      